import type { Dispatch } from '@reduxjs/toolkit';
import type { MouseEvent } from 'react';
import type { NavigateFunction } from 'react-router-dom';
import { FlowTypes, PAM_NEW_ACCOUNT, SELECTED_METHOD_ID } from 'store/constants';
import type { UIPaymentGroups, UIPayout, UIPayoutGroups } from 'store/formatters';
import type {
  CurrencyDisplaySetting,
  Limits,
  MerchantUserFeeResponce,
  PayHubCashierContractsLastDeclineInfoResponse,
  PayHubDomainSessionsEnumsPlatformType,
  PayoutGroupCategories,
  ProcessingCurrency,
} from 'store/models';
import { PayHubAdminPanelDomainPayGroupsEnumsPayGroupActionType } from 'store/models';
import { setSelectedPayGroupId, unlockFlow } from 'store/slices/global';
import { setCurrentMethod } from 'store/slices/withdrawal';
import type { SelectActionWithdrawalMethodProps } from 'utils';
import { getFormattedAmountWithCurrency, getPrettierFormatNumber, softLocalStorageSet } from 'utils';
import { isMethodAvailable } from 'utils/getIsMethodActive';

import type { DynamicTypeObject, TFunction } from '../../commonTypes';
import { sum, truncateNumber } from '../../utils/math/math';
import { calculateUserFee } from '../../utils/userFee';
import type { WagerFeeData } from '../../utils/wagerFee';
import { calculateWagerFee } from '../../utils/wagerFee';
import type { Amount } from '../Deposit/utils';
import { getConversionText } from '../Deposit/utils';
import { redirectToSelectedMethod } from '../routerUtils';
import { Routes } from '../routes';

export const selectActionMethod = (props: SelectActionWithdrawalMethodProps): void => {
  const { method, navigate, dispatch, flowType } = props;
  dispatch(setCurrentMethod(method));
  dispatch(setSelectedPayGroupId(method.id));
  redirectToSelectedMethod(method.id, navigate, flowType);
};

interface SelectWithdrawalMethodHandlerProps {
  method: UIPaymentGroups | UIPayoutGroups;
  platform?: PayHubDomainSessionsEnumsPlatformType;
  dispatch: Dispatch;
  navigate: NavigateFunction;
  flowType: FlowTypes;
}

const METHODS_WITH_FORM: string[] = [
  PayHubAdminPanelDomainPayGroupsEnumsPayGroupActionType.Activity,
  PayHubAdminPanelDomainPayGroupsEnumsPayGroupActionType.NestedList,
  PayHubAdminPanelDomainPayGroupsEnumsPayGroupActionType.Info,
];

export const selectWithdrawalMethodHandler = (
  props: SelectWithdrawalMethodHandlerProps,
  event?: MouseEvent,
): boolean | undefined => {
  const { method, dispatch, navigate, flowType } = props;

  if (!isMethodAvailable(method)) {
    event?.stopPropagation();
    event?.preventDefault();
    dispatch(unlockFlow());
    return false;
  }

  dispatch(setSelectedPayGroupId(method.id));
  softLocalStorageSet(SELECTED_METHOD_ID, JSON.stringify(method.id));

  if (METHODS_WITH_FORM.includes(method.payGroupActionType)) {
    selectActionMethod({
      method,
      dispatch,
      navigate,
      flowType,
    });
  }

  dispatch(unlockFlow());
  return undefined;
};

export const getMaxLimit = (limits: Limits | null, userBalance?: number): number | undefined => {
  if (userBalance && userBalance > (limits?.min || 0)) {
    return (limits?.max && limits?.max > userBalance) || !limits?.max ? userBalance : limits?.max;
  }

  return limits?.max || undefined;
};

interface WithdrawalSubmitTextProps {
  t: TFunction;
  amount: Amount;
  currency: string;
  taxAmount?: number | string;
  processingCurrency?: ProcessingCurrency;
  processingCurrencyRate?: number;
  currencyDisplaySetting?: CurrencyDisplaySetting;
  merchantUserFee?: MerchantUserFeeResponce;
  currencyPrecision?: number;
  fee?: string | null;
  wagerFee?: WagerFeeData | null;
}

interface GetFormattedFeeSubTextProps {
  amount: Amount;
  merchantUserFee?: MerchantUserFeeResponce;
  currencyPrecision?: number;
  fee?: string | null;
  wagerFee?: WagerFeeData | null;
}

const getFeeAmountSubtext = ({ amount, merchantUserFee, currencyPrecision, wagerFee }: GetFormattedFeeSubTextProps) => {
  const amountAsNumber = Number(amount);
  const { wagerFeeAmount } = calculateWagerFee(amountAsNumber, wagerFee, currencyPrecision);
  const { userFeeAmount } = calculateUserFee(amountAsNumber, merchantUserFee, currencyPrecision);

  return truncateNumber(sum([wagerFeeAmount, userFeeAmount]), currencyPrecision, 'up');
};

export const getWithdrawalSubmitSubText = (props: WithdrawalSubmitTextProps) => {
  const {
    t,
    amount,
    currency,
    taxAmount,
    processingCurrency,
    processingCurrencyRate,
    currencyDisplaySetting,
    merchantUserFee,
    currencyPrecision,
    wagerFee,
  } = props;
  const messages: string[] = [];

  if (processingCurrency && processingCurrencyRate) {
    messages.push(
      getConversionText({
        amount,
        processingCurrency,
        processingCurrencyRate,
        currencyDisplaySetting,
        currencyPrecision,
      }),
    );
  }

  const totalFee = getFeeAmountSubtext({
    amount,
    merchantUserFee,
    currencyPrecision,
    wagerFee,
  });

  if (totalFee) {
    const feeText = t('PH.DEPOSIT.LABEL.FEE');
    messages.push(
      `${feeText} ${getFormattedAmountWithCurrency(
        getPrettierFormatNumber(totalFee),
        currency,
        currencyDisplaySetting,
      )}`,
    );
  }

  if (taxAmount) {
    const translation = t('PH.WITHDRAWAL.TOOLTIP.TAX.TITLE');
    messages.push(
      `${translation} ${getFormattedAmountWithCurrency(
        getPrettierFormatNumber(taxAmount),
        currency,
        currencyDisplaySetting,
      )}`,
    );
  }

  return messages.length ? messages.join('; ') : '';
};

interface SetWithdrawalInitDataToStoreProps {
  initData: UIPayout;
  dispatch: Dispatch;
  navigate: NavigateFunction;
  payGroupId?: number;
}

export const setWithdrawalInitDataToStore = (props: SetWithdrawalInitDataToStoreProps) => {
  const { initData, dispatch, navigate, payGroupId } = props;

  if (!payGroupId) {
    return;
  }

  const method = initData.methods.find(({ id }) => id === payGroupId);

  if (!method) {
    return;
  }

  selectWithdrawalMethodHandler(
    {
      method,
      platform: initData?.widgetSettings?.platform,
      dispatch,
      navigate,
      flowType: FlowTypes.withdrawal,
    },
    {} as MouseEvent,
  );
};

export const formatTime = (milliseconds: number): string => {
  if (milliseconds <= 0) {
    return '00:00:00';
  }

  const totalSeconds = Math.floor(milliseconds / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const formattedHours = hours.toString().padStart(2, '0');
  const formattedMinutes = minutes.toString().padStart(2, '0');
  const formattedSeconds = seconds.toString().padStart(2, '0');

  return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
};

export const calculateRemainingTime = (targetDate: Date | null): number => {
  if (!targetDate) {
    return 0;
  }

  const now = new Date();
  return Math.max(0, targetDate.getTime() - now.getTime());
};

export const createDateFromString = (dateString: string | null | undefined): Date | null => {
  if (!dateString) {
    return null;
  }

  const date = new Date(dateString);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const getNewPamOption = (t: (key?: string, options?: DynamicTypeObject, showLoading?: boolean) => string) => ({
  label: t('PH.WITHDRAWAL.PAY_NEW_ACCOUNT'),
  value: PAM_NEW_ACCOUNT,
  selected: false,
});

export const transformLastDeclineInfoToString = (args: PayHubCashierContractsLastDeclineInfoResponse) => {
  const { decline_sub_reason: declineSubReason, comment } = args;
  const declineComment = comment || '';
  const resultSubReason = declineSubReason || '';

  return {
    declineComment,
    declineSubReason: resultSubReason,
  };
};

export const navigateToFirstCategoryMethod = (
  categoryName: string,
  paygroupCategories: PayoutGroupCategories | null | undefined,
  methods: UIPayoutGroups[],
  navigate: NavigateFunction,
  flowType: FlowTypes,
  dispatch?: Dispatch,
  getUserSelectedGroup?: (categoryName: string) => number | null,
): void => {
  const categoryConfig = paygroupCategories?.categories?.find((cat) => cat.category_name === categoryName);

  if (!categoryConfig) return;

  const categoryMethods = methods.filter(
    (method) => categoryConfig.pay_group_ids.includes(method.id) && isMethodAvailable(method),
  );

  if (categoryMethods.length > 0) {
    const savedMethodId = getUserSelectedGroup ? getUserSelectedGroup(categoryName) : null;
    const savedMethod = savedMethodId ? categoryMethods.find((m) => m.id === savedMethodId) : null;
    const methodToNavigate = savedMethod ?? categoryMethods[0];
    dispatch?.(setCurrentMethod(methodToNavigate));
    navigate(`/${flowType}/${methodToNavigate.id}`, {
      state: {
        from: Routes.withdrawal,
      },
      replace: true,
    });
  }
};
